/**
 * Header-based session ID fallback for cross-site deployments (nano.fitracking.com
 * talking to api.tryfi.com). Chrome blocks third-party cookies in this scenario, so the
 * fi.sid session cookie never reaches the API. The backend already supports an
 * x-session-id header as an alternative — this module persists the session ID returned
 * in auth responses to localStorage and attaches it to subsequent requests.
 *
 * Using localStorage rather than an in-memory variable ensures the session ID survives
 * page refreshes.
 *
 * The session ID is also written to a parent-domain cookie (e.g. .fitracking.com) so
 * sibling sites like shop.fitracking.com can read it on navigation. The ecommerce site
 * checks for this cookie on page load and seeds its own localStorage from it.
 */

import Cookies from 'js-cookie';
import { domainConfig } from './domainConfig';

const STORAGE_KEY = 'fi_session_id';

// Cookie domain is configured per-deploy via VITE_SESSION_COOKIE_DOMAIN (see
// cdk/app.ts). Empty string = skip the cookie write entirely (e.g. local dev,
// or any deploy that hasn't explicitly opted in to cross-site session sharing).
function getCookieDomain(): string | undefined {
  return domainConfig.sessionCookieDomain || undefined;
}

function setParentDomainSessionCookie(id: string) {
  const domain = getCookieDomain();
  if (!domain) {
    return;
  }
  Cookies.set(STORAGE_KEY, id, {
    expires: 30,
    domain,
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
    path: '/',
  });
}

// localStorage is the source of truth on this origin, so always overwrite the
// parent-domain cookie at load — otherwise a stale value left by a sibling site
// would survive and break the cross-site handoff.
const existingSessionId = localStorage.getItem(STORAGE_KEY);
if (existingSessionId) {
  setParentDomainSessionCookie(existingSessionId);
}

export function getSessionId() {
  return localStorage.getItem(STORAGE_KEY);
}

export function setSessionId(id: string) {
  localStorage.setItem(STORAGE_KEY, id);
  setParentDomainSessionCookie(id);
}

export function clearSessionId() {
  localStorage.removeItem(STORAGE_KEY);

  // Remove both the parent-domain cookie we set and any legacy host-only cookie
  // older code paths may have left behind, so logout fully resets session state.
  const domain = getCookieDomain();
  if (domain) {
    Cookies.remove(STORAGE_KEY, { domain, path: '/' });
  }
  Cookies.remove(STORAGE_KEY, { path: '/' });
}
